@if (isOpen$ | async) {
  <div
    class="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center sm:p-4 backdrop-blur-sm animate-fade-in"
    (click)="close()"
  >
    <div
      class="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md overflow-hidden max-h-[90vh] flex flex-col"
      (click)="$event.stopPropagation()"
    >
      <!-- Header -->
      <div class="px-5 pt-5 pb-4 flex justify-between items-center border-b border-gray-100">
        <div class="flex items-center gap-3">
          @if ((currentStep$ | async) === 'code') {
            <button
              type="button"
              (click)="goBack()"
              class="text-gray-400 hover:text-gray-600 cursor-pointer p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            >
              <lucide-icon [img]="icons.ArrowLeft" class="w-5 h-5"></lucide-icon>
            </button>
          }
          <h2 class="text-xl font-bold text-gray-900">
            @switch (currentStep$ | async) {
              @case ('email') {
                Forgot Password
              }
              @case ('code') {
                Reset Password
              }
              @case ('success') {
                Success!
              }
            }
          </h2>
        </div>
        <button
          type="button"
          (click)="close()"
          class="text-gray-400 hover:text-gray-600 cursor-pointer p-1.5 hover:bg-gray-100 rounded-full transition-colors"
        >
          <lucide-icon [img]="icons.X" class="w-5 h-5"></lucide-icon>
        </button>
      </div>

      <!-- Content -->
      <div class="px-5 py-6 overflow-y-auto flex-1">
        <!-- Error Message -->
        @if (errorMessage$ | async; as errorMsg) {
          <div
            class="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-5 text-sm font-medium"
          >
            <svg
              class="w-5 h-5 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {{ errorMsg }}
          </div>
        }

        <!-- Step 1: Email -->
        @if ((currentStep$ | async) === 'email') {
          <p class="text-gray-600 mb-6">
            Enter your email address and we'll send you a code to reset your password.
          </p>

          <form [formGroup]="emailForm" (ngSubmit)="onSubmitEmail()" class="space-y-5">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"
                >
                  <lucide-icon [img]="icons.Mail" class="w-5 h-5"></lucide-icon>
                </div>
                <input
                  type="email"
                  formControlName="email"
                  class="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium"
                  [class.border-red-400]="
                    emailForm.get('email')?.invalid && emailForm.get('email')?.touched
                  "
                  placeholder="name@school.edu"
                  autocomplete="email"
                />
              </div>
              @if (emailForm.get('email')?.invalid && emailForm.get('email')?.touched) {
                <p class="mt-2 text-sm text-red-500 font-medium">
                  Please enter a valid email address
                </p>
              }
            </div>

            <button
              type="submit"
              class="w-full bg-brand hover:bg-brand-dark text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-brand/20 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
              [disabled]="isLoading$ | async"
            >
              @if (isLoading$ | async) {
                <svg
                  class="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Sending...
              } @else {
                Send Reset Code
              }
            </button>
          </form>
        }

        <!-- Step 2: Code + New Password -->
        @if ((currentStep$ | async) === 'code') {
          <p class="text-gray-600 mb-6">
            We've sent a 6-digit code to your email. Enter it below along with your new password.
          </p>

          <form [formGroup]="resetForm" (ngSubmit)="onSubmitReset()" class="space-y-5">
            <!-- Code -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Reset Code</label>
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"
                >
                  <lucide-icon [img]="icons.KeyRound" class="w-5 h-5"></lucide-icon>
                </div>
                <input
                  type="text"
                  formControlName="code"
                  class="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium tracking-widest text-center text-lg"
                  [class.border-red-400]="
                    resetForm.get('code')?.invalid && resetForm.get('code')?.touched
                  "
                  placeholder="123456"
                  maxlength="6"
                  autocomplete="one-time-code"
                  inputmode="numeric"
                />
              </div>
              @if (resetForm.get('code')?.invalid && resetForm.get('code')?.touched) {
                <p class="mt-2 text-sm text-red-500 font-medium">Please enter the 6-digit code</p>
              }
            </div>

            <!-- New Password -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"
                >
                  <lucide-icon [img]="icons.Lock" class="w-5 h-5"></lucide-icon>
                </div>
                <input
                  [type]="showPassword() ? 'text' : 'password'"
                  formControlName="newPassword"
                  class="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium"
                  [class.border-red-400]="
                    resetForm.get('newPassword')?.invalid && resetForm.get('newPassword')?.touched
                  "
                  placeholder="Enter new password"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  (click)="togglePasswordVisibility()"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  @if (showPassword()) {
                    <lucide-icon [img]="icons.EyeOff" class="w-5 h-5"></lucide-icon>
                  } @else {
                    <lucide-icon [img]="icons.Eye" class="w-5 h-5"></lucide-icon>
                  }
                </button>
              </div>
              @if (resetForm.get('newPassword')?.invalid && resetForm.get('newPassword')?.touched) {
                <p class="mt-2 text-sm text-red-500 font-medium">
                  Password must be at least 6 characters
                </p>
              }
            </div>

            <!-- Confirm Password -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Confirm Password</label>
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"
                >
                  <lucide-icon [img]="icons.Lock" class="w-5 h-5"></lucide-icon>
                </div>
                <input
                  [type]="showConfirmPassword() ? 'text' : 'password'"
                  formControlName="confirmPassword"
                  class="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium"
                  [class.border-red-400]="
                    (resetForm.get('confirmPassword')?.invalid &&
                      resetForm.get('confirmPassword')?.touched) ||
                    (resetForm.hasError('passwordMismatch') &&
                      resetForm.get('confirmPassword')?.touched)
                  "
                  placeholder="Confirm new password"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  (click)="toggleConfirmPasswordVisibility()"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  @if (showConfirmPassword()) {
                    <lucide-icon [img]="icons.EyeOff" class="w-5 h-5"></lucide-icon>
                  } @else {
                    <lucide-icon [img]="icons.Eye" class="w-5 h-5"></lucide-icon>
                  }
                </button>
              </div>
              @if (
                resetForm.hasError('passwordMismatch') && resetForm.get('confirmPassword')?.touched
              ) {
                <p class="mt-2 text-sm text-red-500 font-medium">Passwords do not match</p>
              }
            </div>

            <button
              type="submit"
              class="w-full bg-brand hover:bg-brand-dark text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-brand/20 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
              [disabled]="isLoading$ | async"
            >
              @if (isLoading$ | async) {
                <svg
                  class="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Resetting...
              } @else {
                Reset Password
              }
            </button>
          </form>
        }

        <!-- Step 3: Success -->
        @if ((currentStep$ | async) === 'success') {
          <div class="text-center py-6">
            <div
              class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5"
            >
              <lucide-icon [img]="icons.CheckCircle" class="w-8 h-8 text-green-600"></lucide-icon>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">Password Reset Complete!</h3>
            <p class="text-gray-600 mb-6">
              Your password has been successfully reset. You can now sign in with your new password.
            </p>
            <button
              type="button"
              (click)="onSuccessClose()"
              class="w-full bg-brand hover:bg-brand-dark text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-brand/20 cursor-pointer"
            >
              Back to Sign In
            </button>
          </div>
        }
      </div>

      <!-- Safe area padding for mobile -->
      <div class="h-safe-area-inset-bottom sm:hidden"></div>
    </div>
  </div>
}

